// src/contexts/UniqueUserIdContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode, useMemo } from 'react';
import { getUniqueUserId } from '../utils/uniqueUserId';
import logger from '../utils/logger';

interface UniqueUserIdContextType {
  uniqueUserId: string | null;
}

// Create the context with a default value
const UniqueUserIdContext = createContext<UniqueUserIdContextType>({
  uniqueUserId: null,
});

interface UniqueUserIdProviderProps {
  children: ReactNode;
}

export const UniqueUserIdProvider: React.FC<UniqueUserIdProviderProps> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Get or generate the unique user ID when the provider mounts
    const id = getUniqueUserId();
    setUserId(id);
    logger.info('UniqueUserIdProvider initialized with ID:', id);
  }, []); // Empty dependency array ensures this runs only once on mount

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({ uniqueUserId: userId }), [userId]);

  return (
    <UniqueUserIdContext.Provider value={value}>
      {children}
    </UniqueUserIdContext.Provider>
  );
};

// Custom hook for easy consumption of the context
export const useUniqueUserId = (): string | null => {
  const context = useContext(UniqueUserIdContext);
  if (context === undefined) {
    throw new Error('useUniqueUserId must be used within a UniqueUserIdProvider');
  }
  return context.uniqueUserId;
};
